import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { ClientIntegrationService } from '../../../services/client-integration.service';
import { ToastService } from '../../../services/toast.service';
import { ReportService } from '../../../services/report.service';
import { Report } from '../../../models/report';
import { DestinationEndpoint } from '../../../models/destination-endpoint';
import { DataSourceType } from '../../../models/data-source-type';
import { SearchTerm } from '../../../models/search-term';
import { takeUntil } from 'rxjs/operators';
import { LOSTalkerTitleService } from '../../../services/lostalker-title-service.service';
import { GridSettings } from 'src/app/features/grid/classes/grid-settings.model';
import { GridColumnData } from 'src/app/features/grid/classes/grid-column-data.class';
import { TypeConstantService } from 'src/app/services/type-constant.service';
import { IntegrationInformation } from 'src/app/models/integration-information';
import { IdentifierService } from 'src/app/services/identifier.service';

@Component({
  selector: 'app-los-data-source-report',
  templateUrl: './los-data-source-report.component.html',
  styleUrls: ['./los-data-source-report.component.scss'],
  providers: [ClientIntegrationService, ReportService, ToastService, LOSTalkerTitleService],
  standalone: false,
})
export class LOSDataSourceReportComponent implements OnInit, OnDestroy {
  componentDestroyed$ = new Subject<boolean>();
  currReport: Report;

  integrationInformationGridSettings = signal<GridSettings>(null);
  public gridLoading = signal<boolean>(true);

  dataSourceTypeItems: DataSourceType[] = [];
  destinationEndpointItems: DestinationEndpoint[] = [];

  filtersChanged = false;
  xmlSearchTerm: SearchTerm;
  jsSearchTerm: SearchTerm;

  integrationInformationData: IntegrationInformation[] = [];
  filteredIntegrationInformationData: IntegrationInformation[] = [];

  // in report
  useJSSearch = false;
  jsLoaded = false;
  exportFileName = 'LOSDataSourceReport';

  dataSourceItemsSelected: DataSourceType[] = []; // Dynamic CSV ...
  destinationEndpointItemsSelected: DestinationEndpoint[] = []; // csd.. lg..

  constructor(
    protected clientService: ClientIntegrationService,
    protected typeConstantService: TypeConstantService,
    protected identifierService: IdentifierService  ,
    protected reportService: ReportService,
    protected toastService: ToastService,
    private titleService: LOSTalkerTitleService,
    private route: ActivatedRoute,
  ) {
    this.xmlSearchTerm = new SearchTerm('xmlConfig', 'Search XML Config...');
    this.jsSearchTerm = new SearchTerm('', 'Search JS...');
  }

  ngOnInit(): void {
    this.route.params
    .pipe(takeUntil(this.componentDestroyed$))
    .subscribe((params) => {

      if (params['reportId'] == null) {
        // get report by url
        this.reportService.getReports()
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe((data) => {
          if (data != null) {
            const report = data.filter(x => x.routerUrl == '/' + this.route.snapshot.url[0].path + '/');
            if (report != null) {
              this.currReport = new Report(report[0]);
              this.integrationInformationGridSettings.set(this.setupGrid());
              this.exportFileName = this.currReport.exportFileName;
              this.titleService.setUpTitle('Report - ' + this.currReport.title);
              this.getReportConstants();
              this.getIntegrationInformation();
            }
            else {
              this.toastService.toastCreate('Report failed to return', 'Danger');
            }
          }
          else {
            this.toastService.toastCreate('Reports failed to return', 'Danger');
          }
        });
      }
      else {
        // get report by id
        this.reportService.getReportById(params['reportId'])
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe((data) => {
          if (data != null) {
            this.currReport = new Report(data);
            this.exportFileName = this.currReport.exportFileName;
            this.titleService.setUpTitle('Report - ' + data.title);
          }
          else {
            this.toastService.toastCreate('Report failed to return', 'Danger');
          }
        },
        (_error) => {
          this.toastService.toastCreate('Report failed to return', 'Danger');
        });
      }
    });
  }

  
  public getReportConstants(): void {
      this.typeConstantService.getReportViewConstantsForReportType(this.currReport.reportTypeId)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        (data) => {
          if (data != null) {
            this.dataSourceTypeItems = data.dataSourceTypes;
            if (this.dataSourceTypeItems.length > 0) {
              this.dataSourceTypeItems.sort((a, b) => a.dataSourceTypeId.localeCompare(b.dataSourceTypeId));
            }

            this.dataSourceItemsSelected = this.dataSourceTypeItems.filter(
              dataSourceType => 
                dataSourceType.dataSourceTypeId == 'DynamicEncompassAPI' 
                || dataSourceType.dataSourceTypeId == 'DynamicByteAPI' 
                || dataSourceType.dataSourceTypeId == 'DynamicLendingQB'
              );
            
            this.destinationEndpointItems = data.destinationEndpoints;
            if (this.destinationEndpointItems.length > 0) {
              this.destinationEndpointItems.sort((a, b) => a.endpointName.localeCompare(b.endpointName));
            }
          }
        },
        (_error) => {
          // console.log(error);
          this.toastService.toastCreate('Report Constants failed to return', 'Danger');
        },
      );
    
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  getIntegrationInformation(): void {
    this.integrationInformationGridSettings().loading.set(true);
    this.identifierService.getAllActiveIntegrationInformationWithTokenInfo()
          .pipe(takeUntil(this.componentDestroyed$))
          .subscribe({
            next: (data) => {
              this.integrationInformationData = data;
              this.integrationInformationData.forEach(intInf => {
                if (intInf.dataSourceType == 'DynamicLendingQB')
                {
                  intInf.losServerName = 'https://secure.mortgage.meridianlink.com';
                  intInf.losUserName = '[LOS Talker Partner ClientId]';
                }
              });
              this.updateFilteredData();
            },
            complete: () => {
              this.integrationInformationGridSettings().loading.set(false);
            },
          });
  }

  clearSearch(item: SearchTerm) {
    item.clear();
    this.updateFilteredData();
  }

  updateSearch(item: SearchTerm) {
    item.save();
    this.updateFilteredData();
  }

  // update filters without the column filters
  public updateFilteredData() {
    this.filteredIntegrationInformationData = [];
    this.filteredIntegrationInformationData = this.integrationInformationData;

    // filter by Integration Filters
    if (this.currReport.hasFilterCards) {
      // data source
      
      if (this.dataSourceItemsSelected.length > 0) {
        const dataSourceIds = this.dataSourceItemsSelected.map(i => i.dataSourceTypeId);
        this.filteredIntegrationInformationData =
          this.filteredIntegrationInformationData.filter(i => dataSourceIds.indexOf(i.dataSourceType) != -1);
      }

      if (this.destinationEndpointItemsSelected.length > 0) {
        const destinationEndpointIds = this.destinationEndpointItemsSelected.map(i => i.destinationEndpointId);
        this.filteredIntegrationInformationData =
          this.filteredIntegrationInformationData.filter(i => destinationEndpointIds.indexOf(i.destinationId) != -1);
      }
    }

    this.integrationInformationGridSettings.set(this.integrationInformationGridSettings().setData(this.filteredIntegrationInformationData));
    this.integrationInformationGridSettings().loading.set(false);
  }

  // reset filters
  public resetFilters(): void {
    this.dataSourceItemsSelected = [];
    this.destinationEndpointItemsSelected = [];
    this.updateFilteredData();
  }
  // apply filters
  public applyFilters(): void {
    this.updateFilteredData();
  }

  setupGrid(): GridSettings {
    return new GridSettings({
      loading: this.gridLoading,
      dataKey: 'integrationId',
      data: this.filteredIntegrationInformationData,
      fixedGrid: true,
      columns: [
        new GridColumnData({ display: false, filterable: false, width: '150px', field: 'clientId', header: 'Client Id'}),
        new GridColumnData({ display: false, filterable: false, width: '270px', field: 'clientInformationId', header: 'Client Information Id'}),
        new GridColumnData({ width: '270px', field: 'clientName', header: 'Client Name'}),
        new GridColumnData({ display: false, filterable: false, width: '270px', field: 'integrationId', header: 'Integration Id'}),
        new GridColumnData({ width: '270px', field: 'integrationName', header: 'IntegrationName'}),
        new GridColumnData({ display: false, filterable: false, width: '200px', field: 'identifierNameValue', header: 'Identifier Name Value'}),
        new GridColumnData({ display: false, filterable: false, field: 'identifierCreatedDate', header: 'Identifier Updated Date', type: 'date', format: 'MM/dd/yyyy h:mm a'}),
        new GridColumnData({ display: false, filterable: false, field: 'identifierUpdatedDate', header: 'Identifier Creation Date', type: 'date', format: 'MM/dd/yyyy h:mm a'}),
        new GridColumnData({ width: '200px', field: 'dataSourceType', header: 'DataSourceType'}),
        new GridColumnData({ width: '150px', field: 'locked', header: 'Locked'}),
        new GridColumnData({ display: false, filterable: false, width: '270px', field: 'lockReason', header: 'Lock Reason'}),
        new GridColumnData({ display: false, filterable: false, width: '150px', field: 'destinationId', header: 'Destination Id'}),
        new GridColumnData({ width: '150px', field: 'destinationName', header: 'EndpointName'}),
        new GridColumnData({ width: '150px', field: 'identifierIdValue', header: 'IdentifierIdValue'}),
        new GridColumnData({ width: '150px', field: 'isMABase', header: 'MA Base'}),
        new GridColumnData({ display: false, filterable: false, width: '150px', field: 'isMABaseCustom', header: 'MA Base Custom'}),
        new GridColumnData({ width: '150px', field: 'selfHosted', header: 'Self Hosted'}),
        new GridColumnData({ display: false, filterable: false, width: '150px', field: 'canFastFetch', header: 'Can FastFetch'}),
        new GridColumnData({ display: false, filterable: false, width: '150px', field: 'canDataSwap', header: 'Can DataSwap'}),
        new GridColumnData({ display: false, filterable: false, width: '150px', field: 'serverId', header: 'Server Id'}),
        new GridColumnData({ width: '200px', field: 'losServerName', header: 'LOS Server Name'}),
        new GridColumnData({ width: '200px', field: 'losUserName', header: 'LOS User Name'}),
        new GridColumnData({ display: false, filterable: false, field: 'integrationUpdatedDate', header: 'Integration Updated Date', type: 'date', format: 'MM/dd/yyyy h:mm a'}),
        new GridColumnData({ display: false, filterable: false, field: 'integrationCreationDate', header: 'Integration Creation Date', type: 'date', format: 'MM/dd/yyyy h:mm a'}),
      ],
      sortMode: 'multiple',
      filter: {
        enabled: this.currReport.hasSearch,
        display: 'row',
        mode: 'row',
      },
      multisort: [
        {
          field: 'updatedDate',
          order: -1,
        },
      ],
      export: {
        enabled: true,
        formats: ['csv'],
        fileName: this.exportFileName,
      },
    });
  }
}
